import React, { useState } from 'react';
import { Modal, ModalContent, ModalHeader, ModalBody, ModalFooter, Button, Card, CardBody, Chip } from "@heroui/react";
import { Calendar, Clock, Check } from "@phosphor-icons/react";
import { format } from 'date-fns';
import { nl } from 'date-fns/locale';

interface AlternateDateSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (selectedDate: Date) => void;
  alternateDates: (Date | string)[];
  candidateName: string;
  isLoading?: boolean;
}

export const AlternateDateSelectionModal: React.FC<AlternateDateSelectionModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  alternateDates,
  candidateName,
  isLoading = false
}) => {
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);

  const handleConfirm = () => {
    if (selectedDate) {
      onConfirm(selectedDate);
    }
  };

  const handleClose = () => {
    setSelectedDate(null);
    onClose();
  };

  const formatDate = (date: Date) => {
    return format(date, "d MMMM yyyy", { locale: nl });
  };

  const formatTime = (date: Date) => {
    return format(date, "HH:mm", { locale: nl });
  };

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={handleClose}
      size="2xl"
      scrollBehavior="inside"
      classNames={{
        backdrop: "bg-neutral-primary/50",
        base: "bg-white",
        header: "border-b border-neutral-primary/10",
        body: "p-6",
        footer: "border-t border-neutral-primary/10"
      }}
    >
      <ModalContent>
        <ModalHeader className="flex flex-col gap-1">
          <div className="flex items-center gap-2">
            <Calendar size={24} className="text-moss-primary" />
            <span className="text-lg font-semibold">Datum selecteren voor bezichtiging</span>
          </div>
          <p className="text-sm text-neutral-600 font-normal">
            Selecteer een datum uit de voorgestelde alternatieven van {candidateName}
          </p>
        </ModalHeader>
        
        <ModalBody>
          <div className="space-y-3">
            <p className="text-sm text-neutral-700 mb-4">
              Kies één van de volgende voorgestelde datums voor de bezichtiging:
            </p>
            
            {alternateDates.map((dateItem, index) => {
              // Convert string to Date object if needed
              const date = typeof dateItem === 'string' ? new Date(dateItem) : dateItem;
              
              return (
                <Card 
                  key={index}
                  isPressable
                  isHoverable
                  className={`cursor-pointer transition-all duration-200 ${
                    selectedDate?.getTime() === date.getTime()
                      ? 'border-2 border-moss-primary bg-moss-primary/5' 
                      : 'border border-neutral-200 hover:border-moss-primary/50'
                  }`}
                  onPress={() => setSelectedDate(date)}
                >
                  <CardBody className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="flex flex-col items-center justify-center w-12 h-12 bg-moss-primary/10 rounded-lg">
                          <Calendar size={20} className="text-moss-primary" />
                        </div>
                        <div className="flex flex-col">
                          <span className="font-medium text-neutral-800">
                            {formatDate(date)}
                          </span>
                          <div className="flex items-center gap-1 text-sm text-neutral-600">
                            <Clock size={14} />
                            <span>{formatTime(date)}</span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        {selectedDate?.getTime() === date.getTime() && (
                          <Chip
                            size="sm"
                            color="success"
                            variant="flat"
                            startContent={<Check size={14} />}
                          >
                            Geselecteerd
                          </Chip>
                        )}
                      </div>
                    </div>
                  </CardBody>
                </Card>
              );
            })}
          </div>
        </ModalBody>
        
        <ModalFooter className="justify-between">
          <Button
            variant="light"
            onPress={handleClose}
            isDisabled={isLoading}
          >
            Annuleren
          </Button>
          <Button
            color="primary"
            onPress={handleConfirm}
            isDisabled={!selectedDate || isLoading}
            isLoading={isLoading}
            className="bg-moss-primary hover:bg-moss-primary/90"
          >
            Bevestigen
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
